import { LocalizationService } from "./services/LocalizationService";
import { IRecurrenceData, MonthlyPatternType, RecurrenceDataDayOfWeek } from "./interfaces/IRecurrenceData";

export class RecurrenceHelper {
  public static getRecurrenceEndType(recurrenceData: IRecurrenceData): RecurrenceEndType {
    if (recurrenceData.repeatForever) {
      return "never";
    }
    if (!!recurrenceData.endDate) {
      return "date";
    }
    return "occurrences";
  }

  public static getLocalizedDay(day: RecurrenceDataDayOfWeek, localizationService: LocalizationService): string {
    switch (day) {
      case "monday":
        return localizationService.strings.DateTimeService_Monday;
      case "tuesday":
        return localizationService.strings.DateTimeService_Tuesday;
      case "wednesday":
        return localizationService.strings.DateTimeService_Wednesday;
      case "thursday":
        return localizationService.strings.DateTimeService_Thursday;
      case "friday":
        return localizationService.strings.DateTimeService_Friday;
      case "saturday":
        return localizationService.strings.DateTimeService_Saturday;
      case "sunday":
        return localizationService.strings.DateTimeService_Sunday;
      default:
        break;
    }
  }
  
  public static getDayKey(date: Date): RecurrenceDataDayOfWeek {
    switch (date.getDay()) {
      case 1:
        return "monday";
      case 2:
        return "tuesday";
      case 3:
        return "wednesday";
      case 4:
        return "thursday";
      case 5:
        return "friday";
      case 6:
        return "saturday";
      case 0:
        return "sunday";
      default:
        break;
    }
  }

  public static getMonthlyRecurrencePatternTypes(startDate: Date | string, localizationService: LocalizationService): IRecurrencePatternOption[] {
    const options: IRecurrencePatternOption[] = [];

    const eventStartDate = new Date(startDate);

    // add option for every specific date of the month
    options.push(this.getDayOfMonthPatternOption(eventStartDate, localizationService));

    // add option for every Nth weekday of the month
    const dayOfWeek = eventStartDate.getDay(); // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfMonth = eventStartDate.getDate(); // Get the day of the month

    // Check if it's the last occurrence of the weekday in the month
    const lastDayOfMonth = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth() + 1, 0); // Last day of the month
    const lastDayOfWeekInMonth = lastDayOfMonth.getDay(); // Day of the week for the last day of the month
    const lastOccurrenceDay = lastDayOfMonth.getDate() - ((lastDayOfWeekInMonth - dayOfWeek + 7) % 7); // Last occurrence of the weekday
    const isLast = dayOfMonth === lastOccurrenceDay;

    options.push(this.getNthWeekdayPatternOption(eventStartDate, localizationService));

    if (isLast) {
      let isLastWeekday = lastDayOfMonth.getDate() === dayOfMonth;
      if (!isLastWeekday) {
        if (lastDayOfMonth.getDay() === 6) {
          isLastWeekday = lastDayOfMonth.getDate() - 1 === dayOfMonth;

        }
        if (lastDayOfMonth.getDay() === 0) {
          isLastWeekday = lastDayOfMonth.getDate() - 2 === dayOfMonth;
        }
      }

      if (isLastWeekday) {
        options.push(this.getLastWeekdayPatternOption(localizationService));
      }
    }

    return options;
  }

  public static getDayOfMonthPatternOption(startDate: Date | string, localizationService: LocalizationService): IRecurrencePatternOption {
    const eventStartDate = new Date(startDate);

    // add option for every specific date of the month
    return {
      text: `${localizationService.strings.Events_RecurrenceMonthlyOnDay} ${eventStartDate.getDate()}`,
      monthlyPattern: "dayOfMonth",
      dayOfWeekInMonth: undefined,
      weekNumberInMonth: undefined
    };
  }

  public static getNthWeekdayPatternOption(startDate: Date | string, localizationService: LocalizationService): IRecurrencePatternOption {
    const eventStartDate = new Date(startDate);
    
    // add option for every Nth weekday of the month
    const dayOfWeek = eventStartDate.getDay();
    const dayOfMonth = eventStartDate.getDate();
    const firstDayOfMonth = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), 1);
    const firstDayOfWeekInMonth = firstDayOfMonth.getDay();
    const firstOccurrenceDay = 1 + ((7 + dayOfWeek - firstDayOfWeekInMonth) % 7);

    const occurrence = Math.floor((dayOfMonth - firstOccurrenceDay) / 7) + 1;

    // Check if it's the last occurrence of the weekday in the month
    const lastDayOfMonth = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth() + 1, 0); // Last day of the month
    const lastDayOfWeekInMonth = lastDayOfMonth.getDay(); // Day of the week for the last day of the month
    const lastOccurrenceDay = lastDayOfMonth.getDate() - ((lastDayOfWeekInMonth - dayOfWeek + 7) % 7); // Last occurrence of the weekday
    const isLast = dayOfMonth === lastOccurrenceDay;

    const occurrenceText = isLast ? localizationService.strings.Events_RecurrenceLast :
      occurrence === 1 ? localizationService.strings.Events_RecurrenceFirst :
      occurrence === 2 ? localizationService.strings.Events_RecurrenceSecond :
      occurrence === 3 ? localizationService.strings.Events_RecurrenceThird :
      occurrence === 4 ? localizationService.strings.Events_RecurrenceFourth :
      "";

    return {
      text: `${localizationService.strings.Events_RecurrenceMonthlyOnThe} ${occurrenceText} ${this.getLocalizedDay(this.getDayKey(eventStartDate), localizationService)}`,
      monthlyPattern: "nthWeekday",
      dayOfWeekInMonth: this.getDayKey(eventStartDate),
      weekNumberInMonth: isLast ? -1 : occurrence
    };
  }

  public static getLastWeekdayPatternOption(localizationService: LocalizationService): IRecurrencePatternOption {
    return {
      text: `${localizationService.strings.Events_RecurrenceMonthlyOnTheLastWeekday}`,
      monthlyPattern: "lastWeekday",
      dayOfWeekInMonth: undefined,
      weekNumberInMonth: undefined
    };
  }
}

export type RecurrenceEndType = "never" | "date" | "occurrences";
export interface IRecurrencePatternOption {
  text: string;
  monthlyPattern: MonthlyPatternType;
  weekNumberInMonth: number;
  dayOfWeekInMonth: RecurrenceDataDayOfWeek;
}
